import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useParams} from "react-router-dom";
import {format, parseISO} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {clearEvent, getEvent} from '../../store/ducks/event/actions';
import {hideFixedFooterContent, hideMobileOptions, showMobileOptions} from 'store/ducks/nav/actions';
import {getEachDayOfInterval} from 'utils/dateUtils';
import {EventState, EventTickets} from 'store/ducks/event/types';
import {Flex} from 'styles';
import PageNotFoundMessage from 'components/pageNotFound/PageNotFoundMessage/PageNotFoundMessage';
import {
	DateContainer,
	DateContent,
	DateSection,
	DateSubTitle,
	DateTitle,
	EventSeatsMobileNavBar,
	NavEventTitle,
	NavIconContainer,
	NavTitle,
	NavTitleContainer,
	TicketPrice,
	TicketPriceText
} from "./styles";
import {ArrowLeft, ChevronRight} from "react-feather";
import {StringUtils} from "../../utils/stringUtils";
import {EventTicketsDay} from "./types";
import Variables from "../../styles/Variables";
import Skeleton from "react-loading-skeleton";
import {formatCurrency} from "../../utils/currencyUtils";

interface StateProps {
	event: EventState,
}

const EventSectionPage = () => {
	const {url} = useParams<{ url: string }>();
	const select = (state: StateProps) => state.event;
	const dispatch = useDispatch();
	const {data, error, loading} = useSelector(select);
	const history = useHistory();
	const _navTitle = 'Selecione uma data';

	const [formattedDates, setFormattedDates] = useState<EventTicketsDay[]>([]);
	const orderedEventDates = useMemo(() => {
		if (!data.eventDates) return [];
		return data.eventDates.sort(
			(a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
		);
	}, [data.eventDates]);

	useEffect(() => {
		dispatch(clearEvent());
		dispatch(hideMobileOptions());
		dispatch(hideFixedFooterContent());
	}, []);

	useEffect(() => {
		dispatch(getEvent(url));
	}, [dispatch]);

	useEffect(() => {
		if (data.url == url) {
			getEventDates();
		}
	}, [data.url]);

	useEffect(() => {
		return () => {
			dispatch(showMobileOptions());
			dispatch(hideFixedFooterContent());
			dispatch(clearEvent());
		}
	}, []);

	const getSkeletonLayout = () => {
		if (loading && !error) {
			return (<>
				<EventSeatsMobileNavBar>
					<NavIconContainer><ArrowLeft/></NavIconContainer>
					<NavTitleContainer>
						<Skeleton height={20} width={150}/>
						<NavTitle>{_navTitle}</NavTitle>
					</NavTitleContainer>
				</EventSeatsMobileNavBar>
				<DateSection>
					<Skeleton height={75}/>
					<Skeleton height={75}/>
					<Skeleton height={75}/>
					<Skeleton height={75}/>
					<Skeleton height={75}/>
					<Skeleton height={75}/>
					<Skeleton height={75}/>
				</DateSection>
			</>)
		}
	}

	const getEventDates = () => {
		if (!orderedEventDates?.length) return;
		const result: EventTicketsDay[] = [];
		orderedEventDates.forEach(event => {
			const start = parseISO(event.startDate);
			const priceRange = getTicketPriceRange(event.tickets);
			result.push({
				eventDateId: event.id,
				day: Number(format(start, 'd')),
				hour: StringUtils.capitalize(format(start, 'HH:mm', {locale: ptBR})),
				month: StringUtils.capitalize(format(start, 'MMMM', {locale: ptBR})),
				weekDay: StringUtils.capitalize(format(start, 'EEE', {locale: ptBR})),
				ticketDescription: priceRange === null ? 'Não há ingressos disponíveis' : getTicketDescription(priceRange),
				ticketId: event.tickets.map(ticket => ticket.id),
			});
		});
		setFormattedDates(result);
	}

	const getTicketPriceRange = (tickets: EventTickets[]) => {
		if (!tickets.length) return null;

		const prices = tickets.map(ticket => ticket.price);

		const minPrice = Math.min(...prices);
		const maxPrice = Math.max(...prices);

		if (minPrice === maxPrice) {
			return minPrice;
		}

		return {
			minPrice, maxPrice
		};
	}

	const getTicketDescription = (price: number | { minPrice: number, maxPrice: number }) => {
		const isFree = price === 0 || typeof price !== 'number' && price.minPrice == 0;
		const fees = isFree ? '' : ' + taxas';
		if (typeof price === "number") {
			return `Preço único de ${formatCurrency(price)}${fees}`
		}
		if (!price.maxPrice) {
			return `Preço único de ${formatCurrency(price.minPrice)}${fees}`
		}
		return `Valores entre ${formatCurrency(price.minPrice)} á ${formatCurrency(price.maxPrice)} + taxas`;
	}

	const setDate = useCallback((eventDateId: number) => {
		const previousPathName = history.location.pathname;
		history.push({pathname: `${previousPathName}/${eventDateId}`});
	}, [])

	const handleBack = () => {
		if (history.length > 1) {
			history.goBack();
		} else {
			history.push(`/:${url}`);
		}
	};

	return (<>
		{error ? <PageNotFoundMessage/> : (<>
			{loading ? getSkeletonLayout() : (<>
					<EventSeatsMobileNavBar>
						<NavIconContainer onClick={handleBack}><ArrowLeft/></NavIconContainer>
						<NavTitleContainer>
							<NavEventTitle>{data.name}</NavEventTitle>
							<NavTitle>{_navTitle}</NavTitle>
						</NavTitleContainer>
					</EventSeatsMobileNavBar>
					<DateSection>
						{formattedDates.map((date) => (
							<DateContainer key={date.eventDateId.toString()} onClick={() => setDate(date.eventDateId)}>
								<DateContent>
									<DateSubTitle>{date.weekDay}</DateSubTitle>
									<DateTitle>{date.day}</DateTitle>
									<DateSubTitle>{date.month}</DateSubTitle>
									<DateSubTitle $textBold>{date.hour}</DateSubTitle>
								</DateContent>
								<TicketPrice>
									<TicketPriceText>{date.ticketDescription}</TicketPriceText>
								</TicketPrice>
								<Flex isWrap={false} alignItems="center" marginRight={12}>
									<ChevronRight color={Variables.colors.dark}/>
								</Flex>
							</DateContainer>))}
					</DateSection>
				</>)}
		</>)}
	</>);
};

export default EventSectionPage;
